import HotelSelection from '../components/booking/HotelSelection';
// ===============================
// Booking.js
// Hotel-Wise Loyalty
// Locked Spin Wheel
// 4-Digit Check-In Code
// ===============================

import React, { useEffect, useMemo, useState, useRef } from "react";
import { app, auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";
import { useNavigate } from "react-router-dom";
import JanakpuriBooking from "./JanakpuriBooking";
import {JANAKPURI_HOTEL_ID} from "../services/janakpuriBookingService";
import {createBookingPaymentClient} from "../services/bookingPaymentService";
import { sendTelegram } from "../telegram";
import "./Booking.css";

const functions = getFunctions(
  app,
  "asia-south1"
);

const PLACEHOLDER_HOTEL = "/images/hotel-placeholder.jpg";
const PLACEHOLDER_ROOM = "/images/room-placeholder.jpg";

const SPIN_OFFERS = [
  {
    id: "offer_5",
    shortLabel: "5%",
    label: "5% OFF",
    type: "percent",
    value: 5,
  },
  {
    id: "offer_10",
    shortLabel: "10%",
    label: "10% OFF",
    type: "percent",
    value: 10,
  },
  {
    id: "offer_15",
    shortLabel: "15%",
    label: "15% OFF",
    type: "percent",
    value: 15,
  },
  {
    id: "offer_20",
    shortLabel: "20%",
    label: "20% OFF",
    type: "percent",
    value: 20,
  },
  {
    id: "offer_100",
    shortLabel: "₹100",
    label: "Flat ₹100 OFF",
    type: "flat",
    value: 100,
  },
  {
    id: "offer_200",
    shortLabel: "₹200",
    label: "Flat ₹200 OFF",
    type: "flat",
    value: 200,
  },
  {
    id: "offer_300",
    shortLabel: "₹300",
    label: "Flat ₹300 OFF",
    type: "flat",
    value: 300,
  },
  {
    id: "offer_luck",
    shortLabel: "Try Again",
    label: "Better Luck Next Time",
    type: "none",
    value: 0,
  },
];

function Booking() {
  const navigate = useNavigate();

  const [arrivalDate, setArrivalDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");

  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [hotels, setHotels] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlans, setShowPlans] = useState(false);

  const [showGallery, setShowGallery] = useState(false);
  const [galleryHotel, setGalleryHotel] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const spinTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(spinTimerRef.current), []);

  const [submitting, setSubmitting] = useState(false);

  // ===============================
  // HELPERS
  // ===============================

  const normalizeHotelName = (name) =>
    String(name || "").trim().toLowerCase();

  const onImgError = (event, fallback) => {
    if (!event?.target) return;

    event.target.onerror = null;
    event.target.src = fallback;
  };

  const generateCheckInCode = () => {
    return String(Math.floor(1000 + Math.random() * 9000));
  };

  const getHotelPoints = (hotelId) => {
    if (!hotelId) return 0;

    return Number(
      userData?.hotelBookingPoints?.[hotelId] || 0
    );
  };

  const selectedHotelPoints = useMemo(() => {
    if (!selectedHotel?.id) return 0;

    return getHotelPoints(selectedHotel.id);
  }, [selectedHotel, userData]);

  const availableSpinPoints = Number(
    userData?.spinPoints || 0
  );

  const isFreeStayAvailable =
    selectedHotelPoints >= 10;

  const originalPrice = Number(
    selectedPlan?.price || 0
  );

  const calculateDiscount = (price, offer) => {
    const safePrice = Number(price || 0);

    if (!offer || safePrice <= 0) {
      return 0;
    }

    if (offer.type === "percent") {
      return Math.round(
        (safePrice * Number(offer.value || 0)) / 100
      );
    }

    if (offer.type === "flat") {
      return Math.min(
        safePrice,
        Number(offer.value || 0)
      );
    }

    return 0;
  };

  const discountAmount = useMemo(() => {
    if (isFreeStayAvailable) return 0;

    return calculateDiscount(
      originalPrice,
      spinResult
    );
  }, [
    originalPrice,
    spinResult,
    isFreeStayAvailable,
  ]);

  const finalPrice = useMemo(() => {
    if (isFreeStayAvailable) return 0;

    return Math.max(
      0,
      originalPrice - discountAmount
    );
  }, [
    originalPrice,
    discountAmount,
    isFreeStayAvailable,
  ]);

  const minimumDate = useMemo(() => {
    const today = new Date();

    const year = today.getFullYear();

    const month = String(
      today.getMonth() + 1
    ).padStart(2, "0");

    const date = String(
      today.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${date}`;
  }, []);

  useEffect(() => {
    let active = true;
    if (!userData?.uid || !selectedHotel?.id || !selectedPlan?.id) return undefined;
    httpsCallable(functions, 'getLockedBookingSpin')({hotelId: selectedHotel.id, planId: selectedPlan.id})
      .then(response => {if (active && response.data.offer) setSpinResult(response.data.offer);})
      .catch(error => console.warn('Spin restoration failed:', error.code));
    return () => {active = false;};
  }, [userData?.uid, selectedHotel?.id, selectedPlan?.id]);

  // ===============================
  // USER LOAD
  // ===============================

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        try {
          if (!user) {
            navigate("/", {
              replace: true,
            });

            return;
          }

          const userSnapshot = await getDoc(
            doc(db, "users", user.uid)
          );

          if (!userSnapshot.exists()) {
            alert(
              "User profile not found. Please login again."
            );

            navigate("/", {
              replace: true,
            });

            return;
          }

          setUserData({
            uid: user.uid,
            ...userSnapshot.data(),
          });
        } catch (error) {
          console.error(
            "User load error:",
            error
          );

          alert(
            "User details load nahi hui: " +
              (error?.message || error)
          );
        } finally {
          setLoadingUser(false);
        }
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  // ===============================
  // HOTELS LOAD
  // ===============================

  useEffect(() => {
    const loadHotels = async () => {
      setLoadingHotels(true);

      try {
        const snapshot = await getDocs(
          collection(db, "hotels")
        );

        let hotelList = snapshot.docs.map(
          (hotelDoc) => ({
            id: hotelDoc.id,
            ...hotelDoc.data(),
          })
        );

        hotelList = hotelList.filter(
          (hotel) =>
            hotel.active === true ||
            String(hotel.active).toLowerCase() ===
              "true"
        );

        hotelList = hotelList.filter(
          (hotel, index, list) => {
            const currentName =
              normalizeHotelName(hotel.name);

            return (
              index ===
              list.findIndex(
                (item) =>
                  normalizeHotelName(item.name) ===
                  currentName
              )
            );
          }
        );

        hotelList.sort(
          (firstHotel, secondHotel) =>
            Number(firstHotel.sort ?? 9999) -
            Number(secondHotel.sort ?? 9999)
        );

        setHotels(hotelList);
      } catch (error) {
        console.error(
          "Hotels load error:",
          error
        );

        alert(
          "Hotels load nahi ho rahe: " +
            (error?.message || error)
        );
      } finally {
        setLoadingHotels(false);
      }
    };

    loadHotels();
  }, []);

  // ===============================
  // HOTEL PLANS
  // ===============================

  const openPlans = async (hotel) => {
    if (isSpinning) return;

    if (spinResult) {
      return alert(
        "Spin result lock ho chuka hai. Ab hotel change nahi kar sakte."
      );
    }

    setSelectedHotel(hotel);
    setSelectedPlan(null);
    if (hotel.id === JANAKPURI_HOTEL_ID) {
      setPlans([]);setShowPlans(false);setLoadingPlans(false);
      return;
    }
    setPlans([]);
    setShowPlans(true);
    setLoadingPlans(true);

    try {
      const snapshot = await getDocs(
        collection(
          db,
          "hotels",
          hotel.id,
          "plans"
        )
      );

      let planList = snapshot.docs.map(
        (planDoc) => ({
          id: planDoc.id,
          ...planDoc.data(),
        })
      );

      planList = planList.filter(
        (plan) =>
          plan.active === true ||
          String(plan.active).toLowerCase() ===
            "true"
      );

      planList.sort(
        (firstPlan, secondPlan) =>
          Number(firstPlan.sort ?? 9999) -
          Number(secondPlan.sort ?? 9999)
      );

      setPlans(planList);
    } catch (error) {
      console.error(
        "Plans load error:",
        error
      );

      alert(
        "Plans load nahi ho rahe: " +
          (error?.message || error)
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  const selectPlan = (plan) => {
    if (isSpinning) return;

    if (
      spinResult &&
      selectedPlan?.id !== plan.id
    ) {
      return alert(
        "Spin result lock ho chuka hai. Ab plan change nahi kar sakte."
      );
    }

    setSelectedPlan(plan);
  };

  const confirmSelectedPlan = () => {
    if (!selectedPlan) {
      return alert(
        "Please select a plan."
      );
    }

    setShowPlans(false);
  };

  // ===============================
  // GALLERY
  // ===============================

  const openHotelGallery = (hotel) => {
    if (!hotel) return;

    const folderName = String(
      hotel.name || ""
    )
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    const images = [];

    for (
      let imageIndex = 1;
      imageIndex <= 15;
      imageIndex += 1
    ) {
      images.push(
        `/images/hotels/${folderName}/${imageIndex}.jpeg`
      );
    }

    setGalleryHotel(hotel);
    setGalleryImages(images);
    setShowGallery(true);
  };

  // ===============================
  // SPIN WHEEL
  // ===============================

  const openSpinWheel = () => {
    if (!selectedHotel) {
      return alert(
        "Pehle hotel select karo."
      );
    }

    if (!selectedPlan) {
      return alert(
        "Pehle stay plan select karo."
      );
    }

    if (isFreeStayAvailable) {
      return alert(
        "Ye stay already free hai. Spin use karne ki zarurat nahi."
      );
    }

    if (availableSpinPoints < 1) {
      return alert(
        "Aapke paas Spin Point available nahi hai."
      );
    }

    if (spinResult) {
      return alert(
        "Spin result already lock ho chuka hai."
      );
    }

    setShowSpinWheel(true);
  };

  const handleSpin = async () => {
    if (isSpinning || spinResult) {
      return;
    }

    if (availableSpinPoints < 1) {
      return alert(
        "Aapke paas Spin Point available nahi hai."
      );
    }

    setIsSpinning(true);
    let selectedOffer;
    try {
      const response = await httpsCallable(functions, 'drawBookingSpin')({hotelId: selectedHotel.id, planId: selectedPlan.id});
      selectedOffer = response.data.offer;
    } catch (error) {setIsSpinning(false); alert('Spin draw nahi hui: ' + error.message); return;}
    const selectedIndex = SPIN_OFFERS.findIndex(offer => offer.id === selectedOffer.id);

    const segmentAngle =
      360 / SPIN_OFFERS.length;

    const selectedSegmentCenter =
      selectedIndex * segmentAngle +
      segmentAngle / 2;

    const currentRotation =
      ((wheelRotation % 360) + 360) % 360;

    const desiredRotation =
      (360 - selectedSegmentCenter) % 360;

    const adjustment =
      (desiredRotation -
        currentRotation +
        360) %
      360;

    const extraRounds = 6 * 360;

    const nextRotation =
      wheelRotation +
      extraRounds +
      adjustment;

    setIsSpinning(true);
    setWheelRotation(nextRotation);

    spinTimerRef.current = window.setTimeout(() => {
      setSpinResult(selectedOffer);
      setIsSpinning(false);
    }, 4200);
  };

  const closeSpinPopup = () => {
    if (isSpinning) return;

    setShowSpinWheel(false);
  };

  // ===============================
  // RAZORPAY TEST CHECKOUT
  // ===============================

  const startRazorpayPayment = createBookingPaymentClient(userData, selectedHotel, selectedPlan);

  // ===============================
  // CREATE BOOKING
  // ===============================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;
    // Janakpuri must never enter the old payment/draft flow, even on a rapid submit.
    if (selectedHotel?.id === JANAKPURI_HOTEL_ID) return;

    if (!userData?.uid) {
      return alert(
        "User not found. Please login again."
      );
    }

    if (!arrivalDate) {
      return alert(
        "Please select arrival date."
      );
    }

    if (!arrivalTime) {
      return alert(
        "Please select arrival time."
      );
    }

    if (!selectedHotel) {
      return alert(
        "Please select a hotel."
      );
    }

    if (!selectedPlan) {
      return alert(
        "Please select a stay plan."
      );
    }

    if (isSpinning) {
      return alert(
        "Spin complete hone ka wait karo."
      );
    }

    setSubmitting(true);

    try {
      const response = await httpsCallable(functions, 'createBookingDraft')({
        hotelId: selectedHotel.id, planId: selectedPlan.id, date: arrivalDate, time: arrivalTime,
      });
      const result = response.data;

      if (result.isHotelFree || result.finalPrice === 0) {
        await sendTelegram(
          `🎁 <b>FREE BOOKING - HOTEL LOYALTY</b>\n` +
            `🆔 <b>${result.bookingId}</b>\n` +
            `🔐 <b>Check-In Code: ${result.checkInCode}</b>\n` +
            `👤 ${userData?.name || "-"}\n` +
            `📞 ${userData?.phoneNumber || "-"}\n` +
            `🏨 ${selectedHotel?.name || "-"}\n` +
            `🕒 ${arrivalDate || "-"} ${arrivalTime || "-"}\n` +
            `📌 ${selectedPlan?.title || "-"}${
              selectedPlan?.hours
                ? ` (${selectedPlan.hours} hrs)`
                : ""
            }\n` +
            `⭐ Hotel Points: ${result.hotelPointsBefore} → -10 = ${result.hotelPointsAfter}\n` +
            `💰 Original Price: ₹${result.originalPrice}\n` +
            `✅ Final Price: FREE`
        );

        alert(
          `🎁 Free Stay Applied!\n\n` +
            `Check-In Code: ${result.checkInCode}\n\n` +
            `Hotel Points: ${result.hotelPointsBefore} → ${result.hotelPointsAfter}`
        );

        navigate("/main", {
          replace: true,
        });

        return;
      }

      /*
       * Paid booking:
       * Booking Firestore me create hone ke turant baad Razorpay Test Checkout.
       * Payment verified hone ke baad hi paid status backend set karta hai.
       */
      await sendTelegram(
        `🆕 <b>NEW ONLINE BOOKING - PAYMENT PENDING</b>\n` +
          `🆔 <b>${result.bookingId}</b>\n` +
          `🔐 <b>Check-In Code: ${result.checkInCode}</b>\n` +
          `👤 ${userData?.name || "-"}\n` +
          `📞 ${userData?.phoneNumber || "-"}\n` +
          `🏨 ${selectedHotel?.name || "-"}\n` +
          `🕒 ${arrivalDate || "-"} ${arrivalTime || "-"}\n` +
          `📌 ${selectedPlan?.title || "-"}${
            selectedPlan?.hours
              ? ` (${selectedPlan.hours} hrs)`
              : ""
          }\n` +
          `💰 Original Price: ₹${result.originalPrice}\n` +
          `${
            result.spinUsed
              ? `🎡 Spin Offer: ${result.spinLabel}\n` +
                `💸 Discount: ₹${result.discountAmount}\n`
              : ""
          }` +
          `💳 Payable: ₹${result.finalPrice}\n` +
          `⏳ Razorpay Payment: Pending`
      );

      const paymentResult =
        await startRazorpayPayment({
          bookingId:
            result.bookingId,

          checkInCode:
            result.checkInCode,

          amount:
            result.finalPrice,
        });

      if (paymentResult?.paid) {
        const verification =
          paymentResult.verification ||
          {};

        await sendTelegram(
          `💳 <b>RAZORPAY PAYMENT VERIFIED ✅</b>\n` +
            `🆔 <b>${result.bookingId}</b>\n` +
            `👤 ${userData?.name || "-"}\n` +
            `📞 ${userData?.phoneNumber || "-"}\n` +
            `🏨 ${selectedHotel?.name || "-"}\n` +
            `💰 Paid: ₹${
              verification.amountPaid ??
              result.finalPrice
            }\n` +
            `💼 Platform Commission: ₹${
              verification.platformCommission ??
              25
            }\n` +
            `🏨 Hotel Net (tracked): ₹${
              verification.hotelSettlementAmount ??
              Math.max(
                0,
                Number(
                  result.finalPrice ||
                  0
                ) - 25
              )
            }\n` +
            `🧪 Mode: TEST`
        );

        alert(
          `✅ Payment Successful!\n\n` +
            `Booking ID: ${result.bookingId}\n` +
            `Check-In Code: ${result.checkInCode}\n` +
            `Paid: ₹${result.finalPrice}\n\n` +
            `Payment backend se verify ho gayi hai.`
        );

        navigate("/main", {
          replace: true,
        });

        return;
      }

      if (paymentResult?.failed) {
        const paymentMessage =
          paymentResult?.error
            ?.description ||
          paymentResult?.error
            ?.reason ||
          "Payment failed.";

        alert(
          `❌ Payment Failed\n\n${paymentMessage}\n\n` +
            `Booking ID: ${result.bookingId}\n` +
            `Booking create ho chuki hai lekin payment pending hai.`
        );
      } else {
        alert(
          `Payment popup close kar diya gaya.\n\n` +
            `Booking ID: ${result.bookingId}\n` +
            `Booking create ho chuki hai lekin payment pending hai.`
        );
      }

      navigate("/main", {
        replace: true,
      });
    } catch (error) {
      console.error(
        "Booking error:",
        error
      );

      alert(
        "Booking error: " +
          (error?.message || error)
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ===============================
  // LOADING
  // ===============================

  if (loadingUser) {
    return (
      <div className="booking-loading">
        Loading user details...
      </div>
    );
  }

  if (!selectedHotel) return <HotelSelection hotels={hotels} loading={loadingHotels} getHotelPoints={getHotelPoints} onSelect={(hotel,plan)=>{openPlans(hotel);if(hotel.id===JANAKPURI_HOTEL_ID && plan)setSelectedPlan(plan);}} />;

  // Janakpuri works directly at /booking as well as at /janakpuri.
  // Rendering here avoids a stale/partial-route handoff to the legacy payment UI.
  if (selectedHotel?.id === JANAKPURI_HOTEL_ID) return <JanakpuriBooking
    initialDate={arrivalDate} initialTime={arrivalTime} initialPlanId={selectedPlan?.id || ''}
    onChangeHotel={() => {setSelectedHotel(null);setSelectedPlan(null);setShowPlans(false);}} />;

  // ===============================
  // UI
  // ===============================

  return (
    <div
      className="booking-container"
      style={{
        backgroundImage:
          "url('/images/bg.jpg')",
      }}
    >
      <div className="booking-card">
        <div className="auth-logo">
          <img
            src="/images/el-logo.png"
            alt="Hotel Dev Palace"
          />
        </div>

        <h2>BOOK A ROOM ONLINE</h2>

        <form onSubmit={handleSubmit}>
          <label>Arrival Date</label>

          <input
            type="date"
            min={minimumDate}
            value={arrivalDate}
            onChange={(event) =>
              setArrivalDate(
                event.target.value
              )
            }
            required
          />

          <label>Arrival Time</label>

          <input
            type="time"
            value={arrivalTime}
            onChange={(event) =>
              setArrivalTime(
                event.target.value
              )
            }
            required
          />

          {/* Hotel list plan select hone ke baad hide */}

          {!selectedPlan && (
            <label>Select Hotel</label>
          )}

          {!selectedPlan &&
            (loadingHotels ? (
              <p className="booking-white-message">
                Loading hotels...
              </p>
            ) : hotels.length === 0 ? (
              <p className="booking-white-message">
                No hotels found.
              </p>
            ) : (
              <div className="hotel-list">
                {hotels.map((hotel) => {
                  const hotelPoints =
                    getHotelPoints(hotel.id);

                  const isSelected =
                    selectedHotel?.id ===
                    hotel.id;

                  return (
                    <div
                      key={hotel.id}
                      className={`hotel-card ${
                        isSelected
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        openPlans(hotel)
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key === " "
                        ) {
                          openPlans(hotel);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        className="hotel-img"
                        src={
                          hotel.imageUrl ||
                          PLACEHOLDER_HOTEL
                        }
                        alt={
                          hotel.name || "Hotel"
                        }
                        onError={(event) =>
                          onImgError(
                            event,
                            PLACEHOLDER_HOTEL
                          )
                        }
                        loading="lazy"
                      />

                      <div className="hotel-info">
                        <div className="hotel-name">
                          {hotel.name || "-"}
                        </div>

                        <div className="hotel-address">
                          {hotel.address || ""}
                        </div>

                        <div className="hotel-action">
                          {isSelected
                            ? "Selected ✅"
                            : "Select"}
                        </div>

                        {/* View Images hotel card ke andar */}

                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();

                            openHotelGallery(
                              hotel
                            );
                          }}
                          style={{
                            marginTop: 8,
                            padding:
                              "7px 13px",
                            border: "none",
                            borderRadius: 999,
                            background:
                              "#0ea5e9",
                            color: "#ffffff",
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          View Images
                        </button>

                        <div
                          className={`hotel-loyalty ${
                            hotelPoints >= 10
                              ? "free-ready"
                              : ""
                          }`}
                        >
                          Loyalty:{" "}
                          {hotelPoints}/10

                          {hotelPoints >= 10
                            ? " • Free Stay Available 🎁"
                            : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

          {/* Hotel aur plan select hone ke baad sirf summary + book */}

          {selectedHotel &&
            selectedPlan && (
              <div className="selected-summary">
                <div>
                  <b>Hotel:</b>{" "}
                  {selectedHotel.name ||
                    "-"}
                </div>

                <div>
                  <b>Plan:</b>{" "}
                  {selectedPlan.title ||
                    "-"}{" "}
                  • ₹{originalPrice}
                </div>

                <div className="summary-loyalty-line">
                  🏨 Hotel Loyalty Points:{" "}
                  {selectedHotelPoints}/10
                </div>

                {isFreeStayAvailable ? (
                  <div className="free-available-message">
                    🎁 Your Next Stay at
                    This Hotel is FREE
                  </div>
                ) : (
                  <div className="required-stays-message">
                    {10 -
                      selectedHotelPoints}{" "}
                    more completed stay(s)
                    required for a free
                    stay.
                  </div>
                )}

                <div className="summary-spin-line">
                  🎡 Available Spin Points:{" "}
                  {availableSpinPoints}
                </div>

                {!isFreeStayAvailable &&
                  availableSpinPoints > 0 &&
                  !spinResult && (
                    <button
                      type="button"
                      className="open-spin-button"
                      onClick={
                        openSpinWheel
                      }
                    >
                      Open Spin Wheel 🎡
                    </button>
                  )}

                {!isFreeStayAvailable &&
                  availableSpinPoints <=
                    0 && (
                    <div className="no-spin-message">
                      Refer a friend and
                      earn 1 Spin Point
                      after their successful
                      check-in.
                    </div>
                  )}

                {spinResult && (
                  <div className="locked-result-summary">
                    <div className="locked-result-title">
                      🎉 Spin Result:{" "}
                      {spinResult.label}
                    </div>

                    <div>
                      Original Price: ₹
                      {originalPrice}
                    </div>

                    <div>
                      Discount: ₹
                      {discountAmount}
                    </div>

                    <div className="locked-final-price">
                      Final Price: ₹
                      {finalPrice}
                    </div>

                    <div className="result-lock-note">
                      🔒 Result Locked —
                      Change or remove nahi
                      ho sakta
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Razorpay Test payment notice */}

          {selectedHotel &&
            selectedPlan &&
            !isFreeStayAvailable && (
              <div className="razorpay-payment-note">
                <div className="razorpay-payment-note-title">
                  🔒 Secure Online Payment
                </div>

                <div>
                  Payable now:{" "}
                  <strong>
                    ₹{finalPrice}
                  </strong>
                </div>

                <div className="razorpay-test-badge">
                  Razorpay TEST MODE
                </div>
              </div>
            )}

          {/* Only booking button */}

          <div className="btn-group">
            <button
              type="submit"
              className="btn-book"
              disabled={
                submitting ||
                isSpinning ||
                !selectedHotel ||
                !selectedPlan
              }
            >
              {submitting
                ? "Booking..."
                : isFreeStayAvailable
                ? "Book Free Stay 🎁"
                : `Pay ₹${finalPrice} & Book`}
            </button>
          </div>
        </form>
      </div>

      {/* ===============================
          PLAN MODAL
      =============================== */}

      {showPlans && (
        <div
          className="popup-overlay"
          onClick={() => {
            if (!isSpinning) {
              setShowPlans(false);
            }
          }}
        >
          <div
            className="popup-content"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="modal-header">
              <h3>
                Choose Time & Price
              </h3>

              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  if (!isSpinning) {
                    setShowPlans(false);
                  }
                }}
              >
                ✕
              </button>
            </div>

            {selectedHotel && (
              <div className="modal-hotel">
                <img
                  src={
                    selectedHotel.imageUrl ||
                    PLACEHOLDER_HOTEL
                  }
                  alt={
                    selectedHotel.name ||
                    "Hotel"
                  }
                  onError={(event) =>
                    onImgError(
                      event,
                      PLACEHOLDER_HOTEL
                    )
                  }
                />

                <div>
                  <div className="mh-name">
                    {selectedHotel.name ||
                      "-"}
                  </div>

                  <div className="mh-address">
                    {selectedHotel.address ||
                      ""}
                  </div>

                  <div className="modal-loyalty">
                    Loyalty:{" "}
                    {selectedHotelPoints}/10
                  </div>
                </div>
              </div>
            )}

            {loadingPlans ? (
              <p>Loading plans...</p>
            ) : plans.length === 0 ? (
              <p>
                No plans available for this
                hotel.
              </p>
            ) : (
              <div className="plan-grid">
                {plans.map((plan) => {
                  const isSelected =
                    selectedPlan?.id ===
                    plan.id;

                  return (
                    <div
                      key={plan.id}
                      className={`plan-card ${
                        isSelected
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        selectPlan(plan)
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key === " "
                        ) {
                          selectPlan(plan);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        src={
                          plan.imageUrl ||
                          PLACEHOLDER_ROOM
                        }
                        alt={
                          plan.title ||
                          "Room Plan"
                        }
                        onError={(event) =>
                          onImgError(
                            event,
                            PLACEHOLDER_ROOM
                          )
                        }
                        loading="lazy"
                      />

                      <div className="plan-meta">
                        <div className="plan-title">
                          {plan.title || "-"}
                        </div>

                        <div className="plan-sub">
                          {plan.hours || "-"}{" "}
                          Hours Stay
                        </div>

                        <div className="plan-price">
                          ₹ {plan.price || 0}
                        </div>

                        {selectedHotelPoints >=
                          10 && (
                          <div className="plan-free-text">
                            🎁 Free Stay
                            Available
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              className="modal-primary"
              onClick={
                confirmSelectedPlan
              }
            >
              Confirm Plan ✅
            </button>
          </div>
        </div>
      )}

      {/* ===============================
          SPIN WHEEL MODAL
      =============================== */}

      {showSpinWheel && (
        <div
          className="spin-popup-overlay"
          onClick={closeSpinPopup}
        >
          <div
            className="spin-popup-box"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <button
              type="button"
              className="spin-popup-close"
              onClick={closeSpinPopup}
              disabled={isSpinning}
            >
              ✕
            </button>

            <div className="spin-popup-heading">
              Spin to Win!
            </div>

            <div className="spin-popup-subheading">
              Use 1 Spin Point and win a
              discount
            </div>

            <div className="wheel-area">
              <div className="wheel-pointer" />

              <div
                className={`spin-wheel ${
                  isSpinning
                    ? "wheel-spinning"
                    : ""
                }`}
                style={{
                  transform: `rotate(${wheelRotation}deg)`,
                }}
              >
                {SPIN_OFFERS.map(
                  (offer, index) => {
                    const angle =
                      index *
                      (360 /
                        SPIN_OFFERS.length);

                    return (
                      <div
                        key={offer.id}
                        className="wheel-label"
                        style={{
                          transform: `rotate(${angle}deg)`,
                        }}
                      >
                        <span>
                          {
                            offer.shortLabel
                          }
                        </span>
                      </div>
                    );
                  }
                )}

                <button
                  type="button"
                  className="wheel-center-button"
                  onClick={handleSpin}
                  disabled={
                    isSpinning ||
                    !!spinResult
                  }
                >
                  {isSpinning
                    ? "..."
                    : "SPIN"}
                </button>
              </div>
            </div>

            {!spinResult ? (
              <button
                type="button"
                className="spin-action-button"
                onClick={handleSpin}
                disabled={isSpinning}
              >
                {isSpinning
                  ? "Spinning... 🎡"
                  : "Use 1 Spin Point"}
              </button>
            ) : (
              <div className="spin-popup-result">
                <div className="spin-popup-result-title">
                  🎉 You Won
                </div>

                <div className="spin-popup-offer">
                  {spinResult.label}
                </div>

                <div className="spin-popup-price-row">
                  <span>
                    Original Price
                  </span>

                  <b>₹{originalPrice}</b>
                </div>

                <div className="spin-popup-price-row">
                  <span>Discount</span>

                  <b>₹{discountAmount}</b>
                </div>

                <div className="spin-popup-price-row final">
                  <span>
                    Final Price
                  </span>

                  <b>₹{finalPrice}</b>
                </div>

                <div className="spin-popup-lock">
                  🔒 Result permanently
                  locked for this booking
                </div>

                <button
                  type="button"
                  className="continue-booking-button"
                  onClick={() =>
                    setShowSpinWheel(false)
                  }
                >
                  Continue Booking ✅
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===============================
          GALLERY MODAL
      =============================== */}

      {showGallery && (
        <div
          className="popup-overlay"
          onClick={() =>
            setShowGallery(false)
          }
        >
          <div
            className="popup-content"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="modal-header">
              <h3>
                {galleryHotel?.name ||
                  "Hotel Gallery"}
              </h3>

              <button
                type="button"
                className="modal-close"
                onClick={() =>
                  setShowGallery(false)
                }
              >
                ✕
              </button>
            </div>

            <div className="gallery-grid">
              {galleryImages.map(
                (image, index) => (
                  <div
                    key={`${image}-${index}`}
                    className="gallery-item"
                  >
                    <img
                      src={image}
                      alt={`room-${
                        index + 1
                      }`}
                      onError={(event) =>
                        onImgError(
                          event,
                          PLACEHOLDER_ROOM
                        )
                      }
                      loading="lazy"
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp */}

      <a
        href="https://wa.me/918810663507?text=Hi%2C%20I%20want%20to%20book%20a%20room"
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Book room on WhatsApp"
      >
        <img
          src="/images/whatsapp.png"
          alt="WhatsApp"
        />
      </a>
    </div>
  );
}

export default Booking;